import PropTypes from 'prop-types';
import cn from 'classnames';
import get from 'lodash/get';

import SectionTitle from 'UI/components/SectionTitle';
import Typography from 'UI/components/Typography';
import Button from 'UI/components/Button';
import Svg from 'UI/components/Svg';
import ContentfulParser from 'components/BlogCommon/Article/ContentfulParser';
import CircularProgress from 'UI/components/CircularProgress';

import { TYPOGRAPHY_SIZE } from 'UI/components/Typography/utils/useTypography';
import { getDocumentFields } from 'utils/helper';
import { useAssessmentSection } from './utils/useAssessmentSection';
import { getRecentageTitle } from './utils/assessmentSectionHelper';

import styles from './styles.module.scss';

const AssessmentSection = (props) => {
  const {
    type,
    title,
    description,
    contentModules,
    currentQuestion,
    totalQuestions,
    answeredCount,
    progress,
    selectedLetter,
    isCompleted,
    result,
    handleSelectAnswer,
    handleNext,
    handleSkip,
    handleOnCTAClick,
    withPercentage,
  } = useAssessmentSection(props);

  if (!contentModules || !totalQuestions) {
    return null;
  }

  return (
    <section className={cn(styles[type], styles.container)}>
      <div className={cn(styles.wrapper, { [styles.result]: isCompleted && result })}>
        {!(isCompleted && result) ? (
          <SectionTitle
            title={title}
            description={description}
            className={styles.title}
          />
        ) : (
          <div className={styles.badge}>YOUR RESULT</div>
        )}

        <div className={styles.card}>
          {!isCompleted && (
            <>
              <div className={styles.progressBlock}>
                <div className={styles.progressHeader}>
                  <Typography
                    size={TYPOGRAPHY_SIZE.paragrapgh14}
                    className={styles.progressCounter}
                  >
                    {answeredCount}
                    /
                    {totalQuestions}
                  </Typography>
                </div>
                <div className={styles.progressBar}>
                  <div
                    className={styles.progressFill}
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              {currentQuestion && (
                <div className={styles.questionBlock}>
                  <Typography
                    isBold
                    size={TYPOGRAPHY_SIZE.headline24}
                    mobileSize={TYPOGRAPHY_SIZE.headline20}
                    className={styles.questionTitle}
                  >
                    {currentQuestion.title}
                  </Typography>

                  <div
                    className={styles.answers}
                    role="radiogroup"
                    aria-label={currentQuestion.title}
                  >
                    {currentQuestion.answers.map(({ title: answerTitle, letter }) => {
                      const inputId = `assessment-answer-${letter}-${answerTitle}`;
                      const isChecked = selectedLetter === letter;

                      return (
                        <label
                          key={`${letter}-${answerTitle}`}
                          htmlFor={inputId}
                          className={cn(styles.answer, {
                            [styles.checked]: isChecked,
                          })}
                        >
                          <input
                            id={inputId}
                            type="radio"
                            name="assessment-answer"
                            value={letter}
                            checked={isChecked}
                            onChange={() => handleSelectAnswer(letter)}
                            className={styles.radioInput}
                          />
                          <span className={styles.radioMark} />
                          <Typography
                            size={TYPOGRAPHY_SIZE.paragrapgh16}
                            className={styles.answerTitle}
                          >
                            {answerTitle}
                          </Typography>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className={styles.actions}>
                <Button
                  secondary
                  onClick={handleSkip}
                  className={styles.skipButton}
                >
                  Skip for now
                </Button>
                <Button
                  onClick={handleNext}
                  disabled={!selectedLetter}
                  className={styles.nextButton}
                >
                  Next
                </Button>
              </div>
            </>
          )}

          {isCompleted && result && (
            <div className={styles.resultBlock}>
              <div className={styles.topContent}>
                <div className={cn({ [styles.resultContent]: withPercentage })}>
                  {result.title && (
                    <Typography
                      isBold
                      size={TYPOGRAPHY_SIZE.headline24}
                      mobileSize={TYPOGRAPHY_SIZE.headline20}
                      className={styles.resultTitle}
                    >
                      {result.title}
                    </Typography>
                  )}
                  {result.text && (
                    <div className={styles.resultText}>
                      <ContentfulParser document={result.text} />
                    </div>
                  )}
                </div>
                {withPercentage && (
                  <CircularProgress
                    percentage={result.startRange}
                    rangeEnd={result.endRange}
                    displayText={getRecentageTitle(result.startRange, result.endRange)}
                    label="capacity lost"
                    color="#ffe603"
                    rangeColor="#ffe603"
                    bgColor="#fafafa"
                    textColor="#171717"
                    size={140}
                    strokeWidth={10}
                  />
                )}
              </div>
              {!!result?.suggestions?.length && (
                <div className={styles.suggestionList}>
                  {result.suggestions.map((item) => {
                    const {
                      title: resultTitle,
                      description: resultDescription,
                      contentList,
                    } = getDocumentFields(item, ['title', 'description', 'contentList']);

                    const icon = get(contentList, '[0]');

                    return (
                      <div className={styles.suggestionContainer}>
                        {icon && (
                          <div className={styles.iconWrapper}>
                            <Svg
                              type={icon}
                              className={styles.icon}
                            />
                          </div>
                        )}
                        <div>
                          <p className={styles.suggestionTitle}>
                            {resultTitle}
                          </p>
                          <p className={styles.suggestionDescription}>
                            {resultDescription}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {result?.link?.buttonTitle && (
                <Button
                  onClick={!result.link.url && handleOnCTAClick}
                  className={styles.ctaButton}
                  href={result.link.url || result.link.slug}
                  data-button
                >
                  {result.link.buttonTitle}
                  <Svg type="arrowRight" />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

AssessmentSection.propTypes = {
  section: PropTypes.instanceOf(Object).isRequired,
  type: PropTypes.string.isRequired,
};

export default AssessmentSection;
